import type { VideoTemplate } from "~/common/videoTemplate/types"
import { useEffect, useRef, useState } from "react"
import { App, Button, Flex, Form, Typography } from "antd"
import { Alert } from "~/components/Alert"
import {
  DEFAULT_VIDEO_RATIO,
  DEFAULT_VIDEO_RESOLUTION,
} from "~/common/constants"
import {
  ResourceUploadScope,
  useResourceUploadStatus,
} from "~/components/ResourceUploadField/UploadScope"
import {
  getTemplateDurationFromVideo,
  getVideoTemplateError,
  updateConfigPrompt,
} from "~/common/videoTemplate/config"
import { useVideoUrlMetadata } from "~/common/media/useVideoUrlMetadata"
import {
  BasicSettings,
  PromptSettings,
  GenerationSettings,
} from "./ConfigSettings"
import { TemplatePreview } from "./TemplatePreview"
import styles from "./index.css"

type TemplateEditorProps = {
  initial: VideoTemplate
  existing: boolean
  onSave: (config: VideoTemplate) => Promise<void>
  onClose: () => void
}

export const TemplateEditor = (props: TemplateEditorProps) => (
  <ResourceUploadScope>
    <EditorContent {...props} />
  </ResourceUploadScope>
)

const EditorContent = ({
  initial,
  existing,
  onSave,
  onClose,
}: TemplateEditorProps) => {
  const [config, setConfig] = useState(() =>
    updateConfigPrompt(
      {
        ...structuredClone(initial),
        ratio: initial.ratio ?? DEFAULT_VIDEO_RATIO,
        resolution: initial.resolution ?? DEFAULT_VIDEO_RESOLUTION,
      },
      initial.prompt,
    ),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")
  const busy = useRef(false)
  const uploads = useResourceUploadStatus()
  const { message, modal } = App.useApp()
  const patch = (value: Partial<VideoTemplate>) =>
    setConfig((previous) => ({ ...previous, ...value }))
  const videoMetadata = useVideoUrlMetadata(config.videoUrl)
  const videoDuration = videoMetadata.metadata?.duration

  // 时长不可配置，始终跟随案例视频时长（并对齐默认模型支持的时长）
  useEffect(() => {
    if (videoDuration === undefined) return

    setConfig((previous) => {
      const duration = getTemplateDurationFromVideo(
        previous.model,
        videoDuration,
      )
      return previous.duration === duration
        ? previous
        : { ...previous, duration }
    })
  }, [videoDuration, config.model])

  const durationHint = !config.videoUrl.trim()
    ? "填入案例视频后自动读取时长"
    : videoMetadata.loading
      ? "正在读取案例视频时长..."
      : videoMetadata.failed
        ? "读取案例视频时长失败，请检查视频链接"
        : "跟随案例视频时长，不可手动修改"

  const close = () => {
    if (busy.current || uploads.isPending()) return
    if (JSON.stringify(config) === JSON.stringify(initial)) {
      onClose()
      return
    }
    modal.confirm({
      title: "放弃尚未保存的修改？",
      content: "关闭后，本次修改将不会保存。",
      okText: "放弃修改",
      cancelText: "继续编辑",
      onOk: () => {
        if (!busy.current && !uploads.isPending()) onClose()
      },
    })
  }
  const save = async () => {
    if (busy.current || uploads.isPending()) return
    if (videoMetadata.loading) {
      setError("正在读取案例视频时长，请稍后保存")
      return
    }
    if (config.videoUrl.trim() && videoMetadata.failed) {
      setError("读取案例视频时长失败，请检查视频链接后再保存")
      return
    }
    const validation = getVideoTemplateError(config)
    if (validation) {
      setError(validation)
      return
    }
    busy.current = true
    setSaving(true)
    setError("")
    try {
      await onSave(config)
      void message.success("模板已保存")
      onClose()
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : "模板保存失败，请重试",
      )
    } finally {
      busy.current = false
      setSaving(false)
    }
  }

  return (
    <Flex
      className={styles.editor}
      vertical
    >
      <Flex
        className={styles.workspace}
        align="stretch"
        gap={24}
      >
        <Form
          className={styles.form}
          layout="vertical"
          requiredMark
          disabled={saving}
          inert={saving}
        >
          <BasicSettings
            config={config}
            patch={patch}
            existing={existing}
            disabled={saving}
            onDownload={(url) =>
              setConfig((previous) =>
                previous.videoUrl === config.videoUrl &&
                previous.downloadUrl === config.downloadUrl
                  ? { ...previous, downloadUrl: url }
                  : previous,
              )
            }
          />
          <PromptSettings
            config={config}
            patch={patch}
            onChange={setConfig}
          />
          <GenerationSettings
            config={config}
            patch={patch}
            durationHint={durationHint}
          />
        </Form>
        <TemplatePreview
          config={config}
          disabled={saving || uploads.pending}
          onCover={(url) =>
            setConfig((previous) =>
              previous.videoUrl === config.videoUrl &&
              previous.videoCover === config.videoCover
                ? { ...previous, videoCover: url }
                : previous,
            )
          }
        />
      </Flex>
      <Flex
        className={styles.editorFooter}
        vertical
        gap={8}
      >
        {error && (
          <Alert
            level="strong"
            showIcon
            title={error}
          />
        )}
        <Flex
          align="center"
          justify="space-between"
          gap={12}
        >
          <Typography.Text type="secondary">
            {uploads.pending
              ? "素材处理中，请稍候…"
              : "保存前可在右侧核对展示内容。"}
          </Typography.Text>
          <Flex gap={8}>
            <Button
              disabled={saving || uploads.pending}
              onClick={close}
            >
              取消
            </Button>
            <Button
              type="primary"
              loading={saving}
              disabled={uploads.pending}
              onClick={() => void save()}
            >
              保存模板
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  )
}
